import { useEffect, useRef, useState } from "react";
import {
  sendTurn,
  startSession as apiStartSession,
  endSession as apiEndSession,
  type ChatMessage,
  type TurnAnalysis,
  type SessionSummary as SummaryT,
  type UserRow,
} from "../lib/api";
import { STT } from "../lib/stt";
import { TTS } from "../lib/tts";
import { SimliAvatar } from "./SimliAvatar";
import { Webcam } from "./Webcam";
import { MicButton } from "./MicButton";
import { CorrectionBubble } from "./CorrectionBubble";
import { SessionSummary } from "./SessionSummary";
import type { SimliAvatarClient } from "../lib/simli";

type Phase = "idle" | "listening" | "thinking" | "speaking";

interface Message extends ChatMessage {
  analysis?: TurnAnalysis | null;
}

const STT_LANG = "multi";

interface Props {
  user: UserRow;
  teacherName: string;
  systemPromptBase: string;
  onExit: () => void;
}

function buildSystemPrompt(base: string, user: UserRow): string {
  const levelHint = user.level
    ? `\n\nThe learner's CEFR level is ${user.level}. Match your vocabulary and grammar complexity to this level.`
    : "";
  const nameHint = user.name ? `\n\nThe learner's name is ${user.name}.` : "";
  const goalHint = user.goal
    ? `\n\nTheir goal is: ${user.goal}. Keep activities relevant.`
    : "";
  return base + levelHint + nameHint + goalHint;
}

export function Lesson({ user, teacherName, systemPromptBase, onExit }: Props): JSX.Element {
  const greeting = user.name
    ? `Hi ${user.name}! Great to see you. What would you like to talk about today?`
    : `Hi! I'm ${teacherName}, your English teacher. What would you like to talk about today?`;

  const systemPrompt = buildSystemPrompt(systemPromptBase, user);

  const [messages, setMessages] = useState<Message[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [interim, setInterim] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(true);
  const [conversationActive, setConversationActive] = useState(false);
  const [avatarReady, setAvatarReady] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryT | null>(null);

  const sttRef = useRef<STT | null>(null);
  const ttsRef = useRef<TTS | null>(null);
  const simliRef = useRef<SimliAvatarClient | null>(null);
  const sttSupported = useRef<boolean>(false);
  const conversationActiveRef = useRef(false);
  const messagesRef = useRef<Message[]>([]);
  const sessionIdRef = useRef<string | null>(null);

  conversationActiveRef.current = conversationActive;
  messagesRef.current = messages;

  useEffect(() => {
    sttSupported.current = STT.isSupported();
    ttsRef.current = new TTS();
    return () => {
      ttsRef.current?.cancel();
      sttRef.current?.abort();
    };
  }, []);

  function handleAvatarReady(client: SimliAvatarClient): void {
    simliRef.current = client;
    ttsRef.current?.setSimli(client);
    setAvatarReady(true);
  }

  function scheduleAutoListen(delayMs = 250): void {
    if (!conversationActiveRef.current) return;
    setTimeout(() => {
      if (conversationActiveRef.current) startListening();
    }, delayMs);
  }

  async function speak(text: string): Promise<void> {
    if (!ttsRef.current) return;
    setPhase("speaking");
    await ttsRef.current.speak(text, {
      onEnd: () => {
        setPhase("idle");
        scheduleAutoListen(300);
      },
      onError: (err) => {
        setError(`TTS: ${err}`);
        setPhase("idle");
        scheduleAutoListen(500);
      },
    });
  }

  function toChatMessages(msgs: Message[]): ChatMessage[] {
    return msgs.map(({ role, content }) => ({ role, content }));
  }

  async function handleUserUtterance(text: string): Promise<void> {
    if (!text.trim()) return;
    setError(null);
    const userMsg: Message = { role: "user", content: text };
    const next: Message[] = [...messagesRef.current, userMsg];
    setMessages(next);
    setPhase("thinking");

    try {
      const payload: ChatMessage[] = [
        { role: "system", content: systemPrompt },
        ...toChatMessages(next),
      ];
      const { reply, analysis } = await sendTurn(payload, {
        sessionId: sessionIdRef.current ?? undefined,
        targetLang: user.target_lang,
        nativeLang: user.native_lang,
      });
      const withAnalysis: Message[] = [
        ...messagesRef.current,
        { ...userMsg, analysis },
        { role: "assistant", content: reply },
      ];
      setMessages(withAnalysis);
      await speak(reply);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("idle");
      scheduleAutoListen(800);
    }
  }

  function startListening(): void {
    if (!sttSupported.current) {
      setError("Speech recognition not supported. Please use Chrome or Edge.");
      setConversationActive(false);
      return;
    }
    setError(null);
    setInterim("");
    ttsRef.current?.cancel();

    const stt = new STT({
      lang: STT_LANG,
      onResult: (text, isFinal) => {
        if (isFinal) {
          setInterim("");
          void handleUserUtterance(text);
        } else {
          setInterim(text);
        }
      },
      onError: (err) => {
        if (err === "no-speech") {
          setPhase("idle");
          scheduleAutoListen(400);
          return;
        }
        if (err === "aborted") {
          setPhase("idle");
          return;
        }
        setError(`STT: ${err}`);
        setPhase("idle");
      },
      onEnd: () => {
        setInterim("");
      },
    });

    sttRef.current = stt;
    stt.start();
    setPhase("listening");
  }

  async function startConversation(): Promise<void> {
    if (!avatarReady) {
      setError("Avatar still loading…");
      return;
    }
    setSummary(null);
    setConversationActive(true);
    setMessages([{ role: "assistant", content: greeting }]);

    try {
      const { session_id } = await apiStartSession({
        targetLang: user.target_lang,
        nativeLang: user.native_lang,
      });
      sessionIdRef.current = session_id;
    } catch (err) {
      setError(`session start failed: ${err instanceof Error ? err.message : String(err)}`);
    }

    void (async () => {
      await speak(greeting);
    })();
  }

  async function endConversation(): Promise<void> {
    setConversationActive(false);
    ttsRef.current?.cancel();
    sttRef.current?.abort();
    setPhase("idle");
    setInterim("");

    const sid = sessionIdRef.current;
    sessionIdRef.current = null;
    if (sid) {
      try {
        const s = await apiEndSession(sid);
        setSummary(s);
      } catch (err) {
        setError(`session end failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }

  function handleMicClick(): void {
    if (!conversationActive) {
      void startConversation();
    } else {
      void endConversation();
    }
  }

  const micState: Phase = !conversationActive ? "idle" : phase;
  const micLabel = !conversationActive
    ? avatarReady ? "Start conversation" : "Loading avatar…"
    : phase === "listening"
      ? "Listening…"
      : phase === "thinking"
        ? "Thinking…"
        : phase === "speaking"
          ? `${teacherName} is speaking…`
          : "End conversation";

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900">
      <header className="px-6 py-3 border-b border-ink-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onExit}
            className="text-xs text-ink-400 hover:text-white border border-ink-600 rounded-lg px-2 py-1"
            title="Back to dashboard"
          >
            ← Dashboard
          </button>
          <div>
            <h1 className="text-lg font-semibold text-white">
              English with {teacherName}
            </h1>
            <p className="text-[10px] text-ink-500 uppercase tracking-widest">
              level {user.level ?? "—"} · {user.target_lang} from {user.native_lang}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {conversationActive && (
            <span className="text-[10px] text-lime-400 uppercase tracking-widest border border-lime-500/40 rounded px-2 py-1">
              ● Live
            </span>
          )}
          <button
            onClick={() => setShowTranscript((v) => !v)}
            className="text-xs text-ink-500 hover:text-white border border-ink-600 rounded-lg px-3 py-1.5 transition"
          >
            {showTranscript ? "Hide transcript" : "Show transcript"}
          </button>
        </div>
      </header>

      <main className="flex-1 grid grid-rows-[1fr_auto] overflow-hidden">
        <section className="relative overflow-hidden">
          <SimliAvatar
            onReady={handleAvatarReady}
            onError={(err) => setAvatarError(err)}
          />

          {!avatarReady && !avatarError && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-ink-600 border-t-lime-400 rounded-full animate-spin" />
                <div className="text-ink-400 text-xs uppercase tracking-widest">
                  Loading avatar…
                </div>
              </div>
            </div>
          )}

          {avatarError && (
            <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
              <div className="bg-red-900/60 border border-red-700 text-red-100 rounded-xl px-4 py-3 text-sm max-w-md text-center">
                Couldnt load avatar: {avatarError}
              </div>
            </div>
          )}

          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-center pointer-events-none">
            <div className="text-xl font-semibold text-white drop-shadow-lg">
              {teacherName}
            </div>
            <div
              className={`text-xs uppercase tracking-widest mt-1 drop-shadow ${
                phase === "listening"
                  ? "text-lime-400"
                  : phase === "speaking"
                    ? "text-blue-300"
                    : phase === "thinking"
                      ? "text-ink-400"
                      : "text-ink-500"
              }`}
            >
              {phase === "listening"
                ? "● listening…"
                : phase === "speaking"
                  ? "● speaking…"
                  : phase === "thinking"
                    ? "● thinking…"
                    : conversationActive ? "ready" : avatarReady ? "tap to start" : "loading…"}
            </div>
          </div>

          <div className="absolute bottom-4 right-4 w-40 sm:w-48 aspect-[4/3] z-10">
            <Webcam />
          </div>

          {interim && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-ink-900/80 border border-lime-500/40 text-white px-4 py-2 rounded-full text-sm max-w-md text-center backdrop-blur">
              "{interim}"
            </div>
          )}

          {phase === "thinking" && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-ink-900/80 border border-ink-600 text-ink-300 px-4 py-2 rounded-full text-sm backdrop-blur">
              <span className="animate-pulse">{teacherName} is thinking…</span>
            </div>
          )}

          {showTranscript && (
            <aside className="absolute top-0 right-0 h-full w-96 bg-ink-900/95 backdrop-blur border-l border-ink-700 overflow-y-auto p-4 space-y-3 scroll-thin">
              <h2 className="text-xs uppercase tracking-widest text-ink-500 mb-2">Transcript</h2>
              {messages.length === 0 && (
                <p className="text-ink-500 text-sm">No messages yet. Tap the microphone.</p>
              )}
              {messages.map((m, i) => (
                <div key={i}>
                  <div
                    className={`rounded-xl px-3 py-2 text-sm ${
                      m.role === "user"
                        ? "bg-lime-500/20 border border-lime-500/40 text-white"
                        : "bg-ink-700 text-white"
                    }`}
                  >
                    <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">
                      {m.role === "user" ? "You" : teacherName}
                    </div>
                    {m.content}
                  </div>
                  {m.role === "user" && m.analysis && (
                    <CorrectionBubble userText={m.content} analysis={m.analysis} />
                  )}
                </div>
              ))}
            </aside>
          )}
        </section>

        <section className="px-4 py-6 flex flex-col items-center gap-3 border-t border-ink-700 bg-ink-900/60">
          <MicButton state={micState} onClick={handleMicClick} customLabel={micLabel} />
          {!conversationActive && avatarReady && (
            <p className="text-ink-500 text-xs text-center max-w-md">
              Tap the microphone once. {teacherName} will greet you, then listen and reply
              automatically. Tap again to end and see your report.
            </p>
          )}
          {error && (
            <div className="bg-red-900/40 border border-red-700 text-red-200 rounded-xl px-4 py-2 text-sm max-w-md">
              {error}
            </div>
          )}
          {!sttSupported.current && (
            <div className="bg-yellow-900/40 border border-yellow-700 text-yellow-200 rounded-xl px-4 py-2 text-xs max-w-md">
              Speech recognition is not supported in this browser. Try Chrome or Edge.
            </div>
          )}
        </section>
      </main>

      {summary && (
        <SessionSummary
          summary={summary}
          onClose={() => setSummary(null)}
          teacherName={teacherName}
        />
      )}
    </div>
  );
}
